/**
 * Uses javascript fetch api to create a session in firebase and get a tokenId
 * Successful fetch will return a JWT Token which will be used in further requests.
 */

import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../App";
import { AUTH_CONSTANTS, LOG_IN_TYPE } from "../constants/auth";
import UserService from "../utils/userService";
import axios from "axios";
import { isTokenExpired, refreshToken } from "./client";

export const fetchFirebaseToken = async (
  token = null, // token recieved from nucleus or google login
  authProvider = LOG_IN_TYPE.nucleus, // to identify whether the token is from nucleus or google
  leadEmployee = false, // to identify user is a lead employee or not
  role = "guest", // role of the user
  uid = null // unique userid
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/firebase/getToken?lead_employee=${leadEmployee}&role=${role}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: token || null,
          "auth-provider": authProvider,
          uid: uid,
        },
      }
    );

    const firebaseResponse = await signInWithCustomToken(
      auth,
      await response.text()
    )
      .then((userCredential) => userCredential)
      .catch((error) => {
        console.log(`[Firebase Token] ${error.code}, ${error.message}`);
        return `${error.code}, ${error.message}`;
      });

    return firebaseResponse;
  } catch (error) {
    return error.message;
  }
};

export const fetchCustomDeviceToken = async (deviceId) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/devices/number/${deviceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const fetchTPSToken = async () => {
  const user = process.env.REACT_APP_TPS_TOKEN;
  const password = process.env.REACT_APP_TPS_SECRET_KEY;
  try {
    const data = JSON.stringify({
      login: user,
      password: password,
    });
    const config = {
      method: "post",
      url: process.env.REACT_APP_MDM_URL + "/rest/public/jwt/login",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };
    const res = await axios(config);
    return res?.data?.id_token;
  } catch (error) {
    console.log(error);
    return null;
  }
};

/**
 * Uses javascript fetch api to to get group id if devices don't have group id
 * takes schoolId as params
 * success: {
 *    group: 45,
 *    status: "OK"
 * }
 *
 * error: {
 *    msg: "Failed to get group id",
 *    status: "FALSE"
 * }
 */
export const setUpSchoolEnrollment = async (schoolId) => {
  let token = UserService.getFirebasetoken();
  const loginType = UserService.getLogInType();

  // refresh Token
  if (!token || isTokenExpired(token)) {
    token = await refreshToken();
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/action/group-id/${schoolId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: token,
          type: loginType,
        },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Uses javascript fetch api to enroll device (TV) to a school
 * takes deviceId, devicetype and schoolId as params
 * success: {
 *    deviceId: "abc1234han182hja" (alphanumeric id),
 *    status: "OK"
 * }
 *
 * error: {
 *    msg: "Failed to enroll device",
 *    status: "FALSE"
 * }
 */
export const fetchTvEnrollment = async (schoolId, deviceId, deviceType) => {
  let token = UserService.getFirebasetoken();
  const loginType = UserService.getLogInType();

  // refresh Token
  if (!token || isTokenExpired(token)) {
    token = await refreshToken();
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/action/device-enrol/${deviceType}/${deviceId}/${schoolId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: token,
          type: loginType,
        },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return error;
  }
};

/**
 * Uses javascript fetch api to trigger action on a device
 * func takes deviceToken or fcmToken, mobile number of the user and the type of action
 * success: {
 *   "status": "FALSE",
 *    "data": {
 *       "multicast_id": 6704276211019212000,
 *       "success": 0,
 *       "failure": 1,
 *       "canonical_ids": 0,
 *       "results": [
 *            {
 *               "error": "InvalidRegistration"
 *           }
 *       ]
 *   }
 * }
 *
 */
export const triggerAction = async (
  deviceToken,
  mobile,
  action,
  projectType,
  actionBody
) => {
  let token = UserService.getFirebasetoken();
  const loginType = UserService.getLogInType();

  // refresh Token
  if (!token || isTokenExpired(token)) {
    token = await refreshToken();
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        authorization: token,
        type: loginType,
      },
      body: JSON.stringify({
        deviceToken,
        mobile: mobile ?? "NA",
        action,
        projectType,
        actionBody: actionBody ?? null,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return error;
  }
};

export const fetchOrderDetails = async (orderId) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/details?orderId=${orderId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const fetchOrders = async (pageNumber, searchOrderId = "", source = "") => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }
  let url = `${process.env.REACT_APP_MDM_URL}/rest/private/order/list?pageNumber=${pageNumber}`;
  if (searchOrderId) {
    url += `&orpOrderId=${encodeURIComponent(searchOrderId)}`;
  }
  if (source) url += `&source=${source}`;
  try {
    const response = await fetch(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const updateDeviceNumberById = async (deviceId, deviceNo, reason) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  const requestBody = {
    deviceNo: deviceNo,
  }
  if (reason && typeof reason === "object") {
    requestBody.triggerReason = JSON.stringify(reason)
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/device/trigger?deviceId=${deviceId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify(requestBody),
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const submitOrderShipment = async (orderId, userId, comments) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/shipment?orderId=${orderId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify({
          comments: comments,
          userId: userId,
        }),
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const fetchLatestDeviceActions = async (deviceId) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/device/progress?deviceId=${deviceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const addDeviceToTPS = async (body) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/devices`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify({ ...body }),
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const setDeviceActionToTPS = async (orderId, deviceId, body) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/public/order/device/action?orderId=${orderId}&deviceNumber=${deviceId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          // authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify({ ...body }),
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const getSignedURL = async (orderId, fileName, mimeType, inputPath) => {
  let token = UserService.getFirebasetoken();
  // refresh Token
  if (!token || isTokenExpired(token)) {
    token = await refreshToken();
  }

  try {
    var myHeaders = new Headers();
    myHeaders.append("x-api-method", "GET");
    myHeaders.append("x-api-mimetype", mimeType);
    myHeaders.append("x-api-order-id", orderId);
    if (inputPath) myHeaders.append("x-api-input-path", inputPath);
    myHeaders.append("x-api-file-name", fileName.toLowerCase());
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Accept", "application/json");
    myHeaders.append("authorization", token);

    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/getSignedURL`,
      {
        method: "GET",
        headers: myHeaders,
      }
    );

    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    console.log(error);
    return error;
  }
};

// To upload the resource files
export const addResourceFiles = async (orderId, mediaFilePaths) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/resources?orderId=${orderId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify({
          key: "shipmentRef",
          value: JSON.stringify(mediaFilePaths),
        }),
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const createOrderDetails = async (orderData) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/create`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify(orderData)
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const deleteOrder = async (orderId) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order?orderId=${orderId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        }
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const cancelShipment = async (orderId, reason) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }
  let createdBy = '';
  const user = JSON.parse(localStorage.getItem("users"));

  if (user && user.email) {
    createdBy = user.email;
  }
  const url = `${process.env.REACT_APP_MDM_URL}/rest/private/order/shipment?orderId=${orderId}&reason=${encodeURIComponent(reason)}&createdBy=${encodeURIComponent(createdBy)}`;

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${tpsToken}`,
        Accept: "application/json",
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error canceling shipment:", error);
    return { status: "ERROR", message: "Network error" };
  }
};

export const getSchoolDetails = async (schoolId) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_GREENFIELD_URL}/api/school/${schoolId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
          "x-api-key": process.env.REACT_APP_SCHOOL_API_KEY
        }
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
};

export const fetchDeviceVitals = async (schoolId) => {
  let token = UserService.getFirebasetoken();
  const loginType = UserService.getLogInType();

  if (!token || isTokenExpired(token)) {
    token = await refreshToken();
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/graphql-v2`, {
      method: 'POST',
      headers: {
        'academicyearid': '4',
        'accept': '*/*',
        'authorization': token,
        'content-type': 'application/json',
        'type': loginType
      },
      body: JSON.stringify({
        operationName: 'GetDeviceVitals',
        variables: { schoolId: parseInt(schoolId) },
        query: 'query GetDeviceVitals($schoolId: Int!) {\n  getDeviceVitals(schoolId: $schoolId) {\n    tabVitals {\n      records {\n        teacherId\n        deviceId\n        teacherMobile\n        fcmToken\n        name\n        status\n      }\n      count\n    }\n  }\n}'
      })
    });
    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const updateOrderDevices = async (orderId, deviceData) => {
  let tpsToken = localStorage.getItem(AUTH_CONSTANTS.TPS_TOKEN);
  if (!tpsToken || isTokenExpired(tpsToken)) {
    const newTpsToken = await fetchTPSToken();

    if (newTpsToken) {
      localStorage.setItem(AUTH_CONSTANTS.TPS_TOKEN, newTpsToken);
      tpsToken = newTpsToken;
    }
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MDM_URL}/rest/private/order/devices?orderId=${orderId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${tpsToken}`,
        },
        body: JSON.stringify(deviceData)
      }
    );
    const responseData = await response?.json();
    return responseData;
  } catch (error) {
    return null;
  }
}